import { RouteShell } from "@/components/route-shell";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ProviderChip } from "@/components/ui/provider-chip";
import { UploadDropzone } from "@/components/upload-dropzone";
import { formatPeriod } from "@/lib/format-period";
import { trpc } from "@/lib/trpc";
import { Link, createFileRoute, useSearch } from "@tanstack/react-router";
import { FileText, Search, Trash2, Users, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

const DOC_STATUSES = ["uploading", "uploaded", "processing", "processed", "error"] as const;

const documentsSearchSchema = z.object({
	status: z.enum(DOC_STATUSES).optional(),
});

export const Route = createFileRoute("/_app/documents/")({
	component: DocumentsPageRoute,
	validateSearch: documentsSearchSchema,
});

function DocumentsPageRoute() {
	return (
		<RouteShell>
			<DocumentsPage />
		</RouteShell>
	);
}

const STATUS_BADGES: Record<string, { className: string; label: string }> = {
	uploading: { className: "bg-[#f3f4f6] text-[#6b7280]", label: "Uploading" },
	uploaded: { className: "bg-[#eef2ff] text-[#4f46e5]", label: "Uploaded" },
	processing: { className: "bg-[#fffbeb] text-[#d97706]", label: "Processing" },
	processed: { className: "bg-[#f0fdf4] text-[#15803d]", label: "Processed" },
	error: { className: "bg-[#fef2f2] text-[#dc2626]", label: "Error" },
};

type DocStatus = "uploading" | "uploaded" | "processing" | "processed" | "error";
type PayrollSystem = "ADP" | "Paylocity" | "SurePayroll" | "Gusto";

function useDebounce<T>(value: T, delay: number): T {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const timer = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(timer);
	}, [value, delay]);
	return debounced;
}

function DocumentsPage() {
	const utils = trpc.useUtils();
	// Seed the status filter from the URL so deep-links from other pages
	// (e.g. /cycle's "Still processing" stage item) land pre-filtered. After
	// mount the filter is driven by local state only — keeping URL-sync
	// scope out of this slice.
	const { status: urlStatus } = useSearch({ from: "/_app/documents/" });
	const [statusFilter, setStatusFilter] = useState<DocStatus | undefined>(urlStatus);
	const [systemFilter, setSystemFilter] = useState<PayrollSystem | undefined>();
	const [searchInput, setSearchInput] = useState("");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const searchRef = useRef<HTMLInputElement>(null);
	const [pendingDelete, setPendingDelete] = useState<{ id: string; fileName: string } | null>(null);

	const debouncedSearch = useDebounce(searchInput, 300);

	const hasFilters = statusFilter || systemFilter || debouncedSearch || dateFrom || dateTo;

	const clearFilters = () => {
		setStatusFilter(undefined);
		setSystemFilter(undefined);
		setSearchInput("");
		setDateFrom("");
		setDateTo("");
	};

	const { data, isLoading } = trpc.documents.list.useQuery(
		{
			status: statusFilter,
			payrollSystem: systemFilter,
			search: debouncedSearch || undefined,
			dateFrom: dateFrom || undefined,
			dateTo: dateTo || undefined,
			page: 1,
			pageSize: 50,
		},
		{ throwOnError: true },
	);

	const deleteMutation = trpc.documents.delete.useMutation({
		onSuccess: () => {
			utils.documents.list.invalidate();
			utils.documents.statusCounts.invalidate();
			toast.success("Document deleted");
		},
		onError: (err) => toast.error(`Delete failed: ${err.message}`),
	});

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-xl font-semibold text-[#1e1e20]">
					Documents{data ? ` (${data.total})` : ""}
				</h1>
				<p className="mt-1 text-sm text-[#6b7280]">
					Upload and manage source documents from ADP, Paylocity, SurePayroll, and Gusto.
				</p>
			</div>

			{/* Search and filters */}
			<div className="flex flex-wrap items-center gap-3">
				<div className="relative min-w-[200px] max-w-sm flex-1">
					<Search
						className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9ca3af]"
						strokeWidth={1.5}
					/>
					<input
						ref={searchRef}
						type="text"
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						placeholder="Search by filename..."
						className="h-9 w-full rounded border border-[#d1d5db] bg-white pl-8 pr-3 text-sm text-[#1e1e20] placeholder:text-[#9ca3af] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
					/>
				</div>
				<select
					value={statusFilter ?? ""}
					onChange={(e) => setStatusFilter((e.target.value || undefined) as DocStatus | undefined)}
					className="h-9 rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
				>
					<option value="">All Statuses</option>
					<option value="uploading">Uploading</option>
					<option value="uploaded">Uploaded</option>
					<option value="processing">Processing</option>
					<option value="processed">Processed</option>
					<option value="error">Error</option>
				</select>
				<select
					value={systemFilter ?? ""}
					onChange={(e) =>
						setSystemFilter((e.target.value || undefined) as PayrollSystem | undefined)
					}
					className="h-9 rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
				>
					<option value="">All Systems</option>
					<option value="ADP">ADP</option>
					<option value="Paylocity">Paylocity</option>
					<option value="SurePayroll">SurePayroll</option>
					<option value="Gusto">Gusto</option>
				</select>
				<input
					type="date"
					value={dateFrom}
					onChange={(e) => setDateFrom(e.target.value)}
					className="h-9 rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
					title="From date"
				/>
				<input
					type="date"
					value={dateTo}
					onChange={(e) => setDateTo(e.target.value)}
					className="h-9 rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
					title="To date"
				/>
				{hasFilters && (
					<button
						type="button"
						onClick={clearFilters}
						className="inline-flex items-center gap-1 rounded px-2 py-1.5 text-xs font-medium text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#1e1e20]"
					>
						<X className="h-3 w-3" strokeWidth={1.5} />
						Clear filters
					</button>
				)}
			</div>

			<UploadDropzone onUploadComplete={() => utils.documents.list.invalidate()} />

			<div className="rounded-lg border border-[#e5e7eb] bg-white">
				{isLoading ? (
					<div className="space-y-0 divide-y divide-[#f3f4f6]">
						<div key="s1" className="h-14 animate-pulse bg-[#f8f9fb]" />
						<div key="s2" className="h-14 animate-pulse" />
						<div key="s3" className="h-14 animate-pulse bg-[#f8f9fb]/50" />
					</div>
				) : !data?.items.length ? (
					<div className="py-12 text-center">
						<p className="text-sm font-semibold text-[#1e1e20]">No documents found</p>
						<p className="mt-1 text-xs text-[#6b7280]">
							{hasFilters
								? "Try adjusting your filters or search terms."
								: "Drag files into the area above to get started."}
						</p>
					</div>
				) : (
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b-2 border-[#e5e7eb] bg-[#f8f9fb] text-left">
								<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
									Document
								</th>
								<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
									Period
								</th>
								<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
									System
								</th>
								<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
									Customer
								</th>
								<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
									Status
								</th>
								<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
									Uploaded
								</th>
								<th className="w-10 px-2 py-2.5" />
							</tr>
						</thead>
						<tbody>
							{data.items.map((doc) => {
								const statusBadge = STATUS_BADGES[doc.status];

								return (
									<tr
										key={doc.id}
										className="border-b border-[#f3f4f6] transition-colors duration-150 last:border-0 hover:bg-[#f8f9fb]"
									>
										<td className="px-4 py-3">
											<Link
												to="/documents/$documentId"
												params={{ documentId: doc.id }}
												className="inline-flex items-center gap-2 font-medium text-[#4f46e5] hover:underline"
											>
												<FileText className="h-4 w-4 shrink-0" strokeWidth={1.5} />
												<span className="truncate">{doc.fileName}</span>
											</Link>
										</td>
										<td className="px-4 py-3 text-[#1e1e20]">{formatPeriod(doc.periodDate)}</td>
										<td className="px-4 py-3">
											<ProviderChip system={doc.payrollSystem} />
										</td>
										<td className="px-4 py-3">
											{doc.customer ? (
												<Link
													to="/customers/$customerId"
													params={{ customerId: doc.customer.id }}
													className="text-[#4f46e5] hover:underline"
												>
													{doc.customer.name}
												</Link>
											) : doc.customerCount > 1 ? (
												<Link
													to="/documents/$documentId"
													params={{ documentId: doc.id }}
													className="inline-flex items-center gap-1 text-xs text-[#6b7280] hover:text-[#4f46e5] hover:underline"
												>
													<Users className="h-3.5 w-3.5" strokeWidth={1.5} />
													{doc.customerCount} customers
												</Link>
											) : (
												<span className="text-xs text-[#9ca3af]">Unassigned</span>
											)}
										</td>
										<td className="px-4 py-3">
											<span
												className={`rounded px-2 py-0.5 text-xs font-medium ${statusBadge?.className ?? ""}`}
											>
												{statusBadge?.label ?? doc.status}
											</span>
										</td>
										<td className="px-4 py-3 text-[#6b7280]">
											{new Date(doc.createdAt).toLocaleDateString()}
										</td>
										<td className="px-2 py-3">
											<button
												type="button"
												onClick={(e) => {
													e.preventDefault();
													setPendingDelete({ id: doc.id, fileName: doc.fileName });
												}}
												disabled={deleteMutation.isPending}
												className="rounded p-1 text-[#9ca3af] hover:bg-[#fef2f2] hover:text-[#dc2626] disabled:opacity-50"
											>
												<Trash2 className="h-4 w-4" strokeWidth={1.5} />
											</button>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				)}
			</div>

			<ConfirmDialog
				open={pendingDelete !== null}
				onOpenChange={(open) => {
					if (!open) setPendingDelete(null);
				}}
				title="Delete document?"
				description={
					pendingDelete
						? `"${pendingDelete.fileName}" will be removed permanently.`
						: "This action cannot be undone."
				}
				confirmLabel="Delete"
				destructive
				isPending={deleteMutation.isPending}
				onConfirm={async () => {
					if (!pendingDelete) return;
					await deleteMutation.mutateAsync({ id: pendingDelete.id });
					setPendingDelete(null);
				}}
			/>
		</div>
	);
}
